import React, { useState, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom";
import Dropdown from 'react-bootstrap/Dropdown';
import brandMark from "../assets/images/logo.jpeg";
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { Card, CardBody, Row, Col, Modal, ModalBody } from 'reactstrap'
import { Button } from 'react-bootstrap';
import { AvForm, AvField } from "availity-reactstrap-validation";
import moment from "moment";
import { get, post } from "../helper/api_helper";
import { adminFetch } from "../helper/admin_api";
import * as Utils from "../Utils";
import { getPageMeta } from "../Utils/pageMeta";
import { useWhatsAppNotifications } from "../context/WhatsAppNotificationContext";
import {
  getDisplayName,
  getNotificationPreview,
} from "../helper/whatsapp_inbox_helper";

function SearchGroup({ title, empty, items, onPick }) {
  return (
    <div className="admin-header__search-group">
      <div className="admin-header__search-label">{title}</div>
      {items.length ? items.map((item) => (
        <Link key={item.key} to={item.to} className="admin-header__search-item" onClick={onPick}>
          <span>{item.label}</span>
          {item.meta ? <small>{item.meta}</small> : null}
        </Link>
      )) : <div className="admin-header__search-empty">{empty}</div>}
    </div>
  );
}

export default function Header({ onSkin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.user);
  const userName = user?.name || user?.user?.name || 'Admin';
  const [isAdd, setIsAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({ products: [], orders: [], customers: [] });
  const [now, setNow] = useState(new Date());
  const pageMeta = getPageMeta(location.pathname);
  const {
    unreadCount,
    recentUnread,
    markAllRead,
  } = useWhatsAppNotifications();

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  const formattedTime = now.toLocaleString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <Link
      to=""
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="dropdown-link"
    >
      {children}
    </Link>
  ));

  const toggleSidebar = (e) => {
    e.preventDefault();
    let isOffset = document.body.classList.contains("sidebar-offset");
    if (isOffset) {
      document.body.classList.toggle("sidebar-show");
    } else {
      if (window.matchMedia("(max-width: 991px)").matches) {
        document.body.classList.toggle("sidebar-show");
      } else {
        document.body.classList.toggle("sidebar-hide");
      }
    }
  }

  const onPassChange = async (e, v) => {
    const currentPassword = String(v?.oldPass || "");
    const newPassword = String(v?.newPass || "");
    const confirmPassword = String(v?.cPass || "");

    if (!currentPassword || !newPassword) {
      toast.error("Current password and new password are required");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must be different from current password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password did not match");
      return;
    }
    if (loading) return;

    const payload = { currentPassword, newPassword };
    const requestPasswordChange = (path) => adminFetch(`${Utils.API_URL}${path}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("adminToken") || user?.token || ""}`,
      },
      body: JSON.stringify(payload),
    });

    setLoading(true);
    try {
      let res;
      try {
        res = await requestPasswordChange("user/admin/change-password");
      } catch (error) {
        if (error?.status !== 404) throw error;
        res = await requestPasswordChange("v1/auth/change-password");
      }

      if (res?.success === false) {
        toast.error(res?.message || "Could not change password");
        return;
      }
      setIsAdd(false);
      toast.success(res?.message || "Password changed successfully");
    } catch (error) {
      toast.error(error?.message || "Could not change password");
    } finally {
      setLoading(false);
    }
  }

  const matchesQuery = (query, values) => {
    const text = query.toLowerCase();
    const digits = text.replace(/\D/g, "");
    return values.some((value) => {
      const raw = String(value || "").toLowerCase();
      if (raw.includes(text)) return true;
      if (!digits) return false;
      return raw.replace(/\D/g, "").includes(digits);
    });
  };

  const handleGlobalSearch = async (e) => {
    e.preventDefault();
    const query = globalSearch.trim();
    if (!query) return;

    setSearchOpen(true);
    setSearchLoading(true);
    try {
      const [productRes, orderRes, userRes] = await Promise.all([
        get("product/list", { search: query, limit: 8 }).catch(() => null),
        post("order/list", { search: query, limit: 8, page: 1, token: user?.token }).catch(() => null),
        get("user/all", { search: query, limit: 8, page: 1, token: user?.token }).catch(() => null),
      ]);

      const productList = productRes?.data?.products || productRes?.products || [];
      const orderList = orderRes?.orders || orderRes?.data?.orders || [];
      const customerList = userRes?.data?.users || userRes?.users || (Array.isArray(userRes?.data) ? userRes.data : []);
      const products = (Array.isArray(productList) ? productList : [])
        .filter((item) => matchesQuery(query, [item?.name, item?.title]))
        .slice(0, 5);
      const orders = (Array.isArray(orderList) ? orderList : [])
        .filter((item) => matchesQuery(query, [
          item?.orderNumber,
          item?.invoiceNumber,
          item?.shippingAddress?.firstName,
          item?.shippingAddress?.lastName,
          item?.shippingAddress?.email,
          item?.user?.email,
          item?.shippingAddress?.phone,
          item?.address?.phone,
        ]))
        .slice(0, 5);
      const customers = (Array.isArray(customerList) ? customerList : [])
        .filter((item) => matchesQuery(query, [item?.name, item?.userName, item?.email, item?.phone, item?.mobile]))
        .slice(0, 5);

      setSearchResults({ products, orders, customers });
    } finally {
      setSearchLoading(false);
    }
  };

  const openWhatsAppInbox = (waId) => {
    if (waId) {
      navigate(`/whatsapp-inbox?waId=${encodeURIComponent(waId)}`);
      return;
    }
    navigate("/whatsapp-inbox");
  };

  const handleMarkAllRead = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await markAllRead();
  };

  return (
    <div className="header-main px-3 px-lg-4 admin-header">
      <Link onClick={toggleSidebar} className="menu-link me-2 me-lg-3" aria-label="Toggle menu">
        <i className="ri-menu-2-fill"></i>
      </Link>

      <div className="admin-header__title me-3 d-none d-md-block">
        <div className="admin-header__section text-secondary small">{pageMeta.section}</div>
        <h1 className="admin-header__page-title mb-0">{pageMeta.title}</h1>
      </div>

      <form className="admin-header__search me-auto d-none d-lg-flex" onSubmit={handleGlobalSearch}>
        <div className="form-search admin-header__search-field">
          <input
            type="search"
            className="form-control"
            placeholder="Orders, customers, products…"
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            aria-label="Global search"
            onFocus={() => {
              if (searchResults.products.length || searchResults.orders.length || searchResults.customers.length) {
                setSearchOpen(true);
              }
            }}
          />
          <i className="ri-search-line"></i>
          {searchOpen && (
            <div className="admin-header__search-results">
              {searchLoading ? (
                <div className="admin-header__search-empty">Searching…</div>
              ) : (
                <>
                  <SearchGroup
                    title="Orders"
                    empty="No matching orders"
                    items={searchResults.orders.map((order) => ({
                      key: order._id || order.orderNumber,
                      label: order.orderNumber || "Order",
                      meta: [order.shippingAddress?.firstName, order.shippingAddress?.lastName].filter(Boolean).join(" "),
                      to: `/order?search=${encodeURIComponent(order.orderNumber || globalSearch.trim())}`,
                    }))}
                    onPick={() => setSearchOpen(false)}
                  />
                  <SearchGroup
                    title="Customers"
                    empty="No matching customers"
                    items={searchResults.customers.map((customer) => ({
                      key: customer._id || customer.email,
                      label: customer.name || customer.userName || "Customer",
                      meta: customer.phone || customer.mobile || "",
                      to: `/registered?search=${encodeURIComponent(customer.phone || customer.email || customer.name || globalSearch.trim())}`,
                    }))}
                    onPick={() => setSearchOpen(false)}
                  />
                  <SearchGroup
                    title="Products"
                    empty="No matching products"
                    items={searchResults.products.map((product) => ({
                      key: product._id,
                      label: product.name || product.title || "Product",
                      meta: "",
                      to: `/product?search=${encodeURIComponent(product.name || product.title || globalSearch.trim())}`,
                    }))}
                    onPick={() => setSearchOpen(false)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </form>

      <div className="admin-header__actions d-flex align-items-center gap-2 gap-lg-3 ms-auto">
        <span className="admin-header__datetime d-none d-xl-inline text-secondary small">
          <i className="ri-time-line me-1" />
          {formattedTime}
        </span>

        <Dropdown className="dropdown-notification" align="end">
          <Dropdown.Toggle as={CustomToggle} className="admin-header__icon-btn p-0 border-0">
            <span className="admin-header__icon-btn" title="Notifications" aria-label="Notifications">
              <i className="ri-notification-3-line" />
              {unreadCount > 0 && (
                <span className="admin-header__badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="mt-10-f">
            <div className="dropdown-menu-header d-flex align-items-center justify-content-between">
              <h6 className="dropdown-menu-title mb-0">WhatsApp</h6>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="admin-header__text-btn"
                  onClick={handleMarkAllRead}
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="list-group list-group-flush">
              {recentUnread.length === 0 ? (
                <div className="px-3 py-3 text-secondary small">No unread WhatsApp messages</div>
              ) : (
                recentUnread.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className="list-group-item list-group-item-action text-start border-0"
                    onClick={() => openWhatsAppInbox(item.waId)}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <span className="avatar avatar-sm bg-success-subtle text-success rounded-circle d-inline-flex align-items-center justify-content-center">
                        <i className="ri-whatsapp-line" />
                      </span>
                      <div className="list-group-body flex-grow-1">
                        <strong>{getDisplayName(item.contactName, item.waId)}</strong>
                        <div className="small text-secondary">
                          {getNotificationPreview(item.body, 72)}
                        </div>
                        <div className="small text-muted mt-1">
                          {item.createdAt ? moment(item.createdAt).fromNow() : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="dropdown-menu-footer border-top p-2">
              <button
                type="button"
                className="btn btn-sm btn-light w-100"
                onClick={() => openWhatsAppInbox()}
              >
                Open WhatsApp Inbox
              </button>
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="dropdown-profile" align="end">
          <Dropdown.Toggle as={CustomToggle}>
            <div className="avatar online brand-mark">
              <img src={brandMark} alt="Careveli" />
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu className="mt-10-f">
            <div className="dropdown-menu-body">
              <div className="avatar avatar-xl online brand-mark mb-3"><img src={brandMark} alt="Careveli" /></div>
              <h5 className="mb-1 text-dark fw-semibold">{userName}</h5>
              <p className="fs-sm text-secondary">{user?.role || user?.user?.role}</p>
              <nav className="nav">
                <Link to="" onClick={(e) => { e.preventDefault(); setIsAdd(true); }}>
                  <i className="ri-lock-password-line"></i> Change Password
                </Link>
                <Link to="/logout"><i className="ri-logout-box-r-line"></i> Log Out</Link>
              </nav>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <Modal isOpen={isAdd} centered>
        <ModalBody>
          <Card>
            <CardBody>
              <AvForm onValidSubmit={onPassChange}>
                <div className='mb-4 mt-4'>
                  <AvField
                    name="oldPass"
                    label="Old password"
                    type="password"
                    placeholder="Enter old password"
                    required
                  />
                  <AvField
                    name="newPass"
                    label="New password"
                    type="password"
                    placeholder="Enter new password"
                    required
                  />
                  <AvField
                    name="cPass"
                    label="Confirm password"
                    type="password"
                    placeholder="Enter confirm password"
                    required
                  />
                </div>
                <Row>
                  <Col md={6}>
                    <button
                      className="btn btn-dark w-100 waves-effect waves-light"
                      style={{ border: 'none', backgroundColor: Utils.themeColor }}
                      onClick={() => setIsAdd(false)}
                      type="reset"
                    >
                      Cancel
                    </button>
                  </Col>
                  <Col md={6}>
                    <button
                      className="btn btn-dark w-100 waves-effect waves-light"
                      style={{ border: 'none', backgroundColor: Utils.themeColor }}
                      type="submit"
                    >
                      {currentData?._id ? 'Update' : 'Add'}
                    </button>
                  </Col>
                </Row>
              </AvForm>
            </CardBody>
          </Card>
        </ModalBody>
      </Modal>
    </div>
  )
}
